import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { api } from '@/App';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import ReminderBell from '@/components/ReminderBell';
import { ShoppingCart, Plus, Minus, Trash2, Send, Search, X } from 'lucide-react';

export default function UserDashboard({ user, onLogout, onUserUpdate }) {
  const [items, setItems] = useState([]);
  const [basket, setBasket] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBasket, setShowBasket] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [quantities, setQuantities] = useState({}); // Track quantity for each item
  const [editedQuantities, setEditedQuantities] = useState({}); // Track which items have been manually edited
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showOrderConfirm, setShowOrderConfirm] = useState(false);
  const { t, setLanguage } = useLanguage();

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    if (onUserUpdate) {
      onUserUpdate({ ...user, language: newLang });
    }
  };

  // Handle browser back button when basket is open
  useEffect(() => {
    if (showBasket) {
      // Push a state when basket opens so back button closes it
      window.history.pushState({ basketOpen: true }, '');
      
      const handlePopState = (event) => {
        // When back is pressed, close basket instead of navigating away
        setShowBasket(false);
      };
      
      window.addEventListener('popstate', handlePopState);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [showBasket]);

  useEffect(() => {
    loadItems();
    loadBasket();
  }, []);

  const loadItems = async () => {
    try {
      const res = await api.get('/my-items');
      setItems(res.data);
      // Initialize quantities for each item
      const initialQuantities = {};
      res.data.forEach(item => {
        initialQuantities[item.item_id] = 1;
      });
      setQuantities(initialQuantities);
    } catch (error) {
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const loadBasket = async () => {
    try {
      const res = await api.get('/basket');
      if (res.data.basket && res.data.basket.length > 0) {
        setBasket(res.data.basket);
      }
    } catch (error) {
      console.error('Failed to load basket:', error);
    }
  };

  const saveBasket = async (newBasket) => {
    try {
      await api.put('/basket', { basket: newBasket });
    } catch (error) {
      console.error('Failed to save basket:', error);
    }
  };

  const updateQuantity = (item_id, value) => {
    const quantity = Math.max(1, parseInt(value) || 1);
    setQuantities({ ...quantities, [item_id]: quantity });
    setEditedQuantities({ ...editedQuantities, [item_id]: true });
  };

  // Handle focus on quantity input - clear if showing default value
  const handleQuantityFocus = (item_id, e) => {
    if (!editedQuantities[item_id]) {
      // Default value - clear the field
      setQuantities({ ...quantities, [item_id]: '' });
    } else {
      // User has entered a value - select all text and move cursor to end
      e.target.select();
    }
  };

  // Handle blur on quantity input - restore default if empty
  const handleQuantityBlur = (item_id) => {
    const currentValue = quantities[item_id];
    if (currentValue === '' || currentValue === undefined || currentValue === null) {
      // Reset to default
      setQuantities({ ...quantities, [item_id]: 1 });
      setEditedQuantities({ ...editedQuantities, [item_id]: false });
    }
  };

  // Handle quantity change - allow empty during editing
  const handleQuantityChange = (item_id, value) => {
    if (value === '') {
      setQuantities({ ...quantities, [item_id]: '' });
      setEditedQuantities({ ...editedQuantities, [item_id]: true });
    } else {
      const quantity = Math.max(1, parseInt(value) || 1);
      setQuantities({ ...quantities, [item_id]: quantity });
      setEditedQuantities({ ...editedQuantities, [item_id]: true });
    }
  };

  const addToBasket = (item) => {
    const qty = quantities[item.item_id];
    const quantity = (qty === '' || qty === undefined || qty === null) ? 1 : qty;
    const existing = basket.find(b => b.item_id === item.item_id);
    
    let newBasket;
    if (existing) {
      newBasket = basket.map(b =>
        b.item_id === item.item_id 
          ? { ...b, quantity: b.quantity + quantity } 
          : b
      );
    } else {
      newBasket = [...basket, { 
        item_id: item.item_id, 
        item_name: item.item_name, 
        vendor_name: item.vendor_name,
        units: item.units || '',
        quantity: quantity 
      }];
    }
    setBasket(newBasket);
    saveBasket(newBasket);
    toast.success(`Added ${quantity} × ${item.item_name} to basket`, { duration: 2000 });
  };

  // Helper function to calculate total units
  const calculateTotalUnits = (units, quantity) => {
    if (!units) return quantity;
    // Try to extract numeric value from units (e.g., "Box of 100" -> 100)
    const numbers = units.match(/\d+/);
    if (numbers) {
      return parseInt(numbers[0]) * quantity;
    }
    return quantity;
  };

  const updateBasketQuantity = (item_id, delta) => {
    const newBasket = basket.map(b => {
      if (b.item_id === item_id) {
        const newQty = b.quantity + delta;
        return newQty > 0 ? { ...b, quantity: newQty } : b;
      }
      return b;
    }).filter(b => b.quantity > 0);
    setBasket(newBasket);
    saveBasket(newBasket);
  };

  const removeFromBasket = (item_id) => {
    const newBasket = basket.filter(b => b.item_id !== item_id);
    setBasket(newBasket);
    saveBasket(newBasket);
    toast.success('Removed from basket', { duration: 2000 });
  };

  const handleCheckout = () => {
    if (basket.length === 0) {
      toast.error('Basket is empty');
      return;
    }
    setShowOrderConfirm(true);
  };

  const handleConfirmCheckout = async () => {
    setOrdering(true);
    setShowOrderConfirm(false);
    try {
      await api.post('/checkout', {
        items: basket.map(b => ({ item_id: b.item_id, quantity: b.quantity }))
      });
      toast.success('Order placed successfully!');
      setBasket([]);
      // Basket is cleared on backend after successful order
      setShowBasket(false);
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setOrdering(false);
    }
  };

  // Separate top 10 most ordered items and regular items
  const sortedItems = [...items].sort((a, b) => b.order_count - a.order_count);
  const top10Items = sortedItems.slice(0, 10);
  const regularItems = items.filter(item => !top10Items.find(t => t.item_id === item.item_id));

  // Group items by vendor and sort alphabetically
  const groupItemsByVendor = (itemsList) => {
    const grouped = itemsList.reduce((acc, item) => {
      if (!acc[item.vendor_name]) {
        acc[item.vendor_name] = [];
      }
      acc[item.vendor_name].push(item);
      return acc;
    }, {});

    // Sort items within each vendor alphabetically
    Object.keys(grouped).forEach(vendor => {
      grouped[vendor].sort((a, b) => a.item_name.localeCompare(b.item_name));
    });

    return grouped;
  };

  // Apply search filter
  const filterItems = (itemsList) => {
    if (!searchQuery) return itemsList;
    return itemsList.filter(item => 
      item.item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.item_description && item.item_description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.sku && item.sku.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  const filteredTop10 = filterItems(top10Items);
  const filteredRegular = filterItems(regularItems);
  
  const groupedTop10 = groupItemsByVendor(filteredTop10);
  const groupedRegular = groupItemsByVendor(filteredRegular);

  const renderItemsGroup = (groupedItems, title, isTopItems = false) => {
    const sortedVendors = Object.keys(groupedItems).sort();
    
    if (sortedVendors.length === 0) return null;

    return (
      <div className="space-y-4">
        {title && (
          <div className="flex items-center space-x-2 mb-4">
            <h2 className="text-xl md:text-xl text-sm font-semibold text-gray-900">{title}</h2>
          </div>
        )}
        
        {sortedVendors.map((vendorName) => (
          <div key={vendorName} className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <span className="w-1.5 h-6 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></span>
                <span>{vendorName}</span>
                <span className="text-sm font-normal text-gray-600">({groupedItems[vendorName].length} {t('nav.items').toLowerCase()})</span>
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {groupedItems[vendorName].map((item) => (
                <div
                  key={item.item_id}
                  className="px-4 py-4 hover:bg-gray-50 transition-colors"
                  data-testid={`item-row-${item.item_id}`}
                >
                  {/* Desktop View */}
                  <div className="hidden md:flex justify-between items-center">
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{item.item_name}</p>
                        {item.item_description && (
                          <p className="text-xs text-gray-500 mt-1">{item.item_description}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('item.sku')}</p>
                        <p className="text-sm text-gray-900 mt-1">{item.sku || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('item.units')}</p>
                        <p className="text-sm text-gray-900 mt-1">{item.units || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 ml-4">
                      <div className="flex items-center space-x-2">
                        <label className="text-xs text-gray-500 uppercase tracking-wide">{t('order.qty')}</label>
                        <Input
                          type="number"
                          min="1"
                          max="999"
                          value={quantities[item.item_id] ?? 1}
                          onChange={(e) => handleQuantityChange(item.item_id, e.target.value)}
                          onFocus={(e) => handleQuantityFocus(item.item_id, e)}
                          onBlur={() => handleQuantityBlur(item.item_id)}
                          className="w-16 h-8 text-center text-sm"
                          data-testid={`quantity-input-${item.item_id}`}
                        />
                      </div>
                      <Button
                        onClick={() => addToBasket(item)}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all h-8 px-3"
                        data-testid={`add-to-basket-${item.item_id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('common.add')}
                      </Button>
                    </div>
                  </div>

                  {/* Mobile View */}
                  <div className="md:hidden space-y-3">
                    {/* Line 1: Item name and units */}
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">{item.item_name}</p>
                        {item.item_description && (
                          <p className="text-xs text-gray-500 mt-1">{item.item_description}</p>
                        )}
                      </div>
                      <div className="ml-3 text-right">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">{t('item.units')}</p>
                        <p className="text-sm text-gray-900">{item.units || '-'}</p>
                      </div>
                    </div>
                    
                    {/* Line 2: Quantity input and Add button */}
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2 flex-1">
                        <label className="text-xs text-gray-500 uppercase tracking-wide whitespace-nowrap">{t('order.qty')}</label>
                        <Input
                          type="number"
                          min="1"
                          max="999"
                          value={quantities[item.item_id] ?? 1}
                          onChange={(e) => handleQuantityChange(item.item_id, e.target.value)}
                          onFocus={(e) => handleQuantityFocus(item.item_id, e)}
                          onBlur={() => handleQuantityBlur(item.item_id)}
                          className="w-20 h-9 text-center text-sm"
                          data-testid={`quantity-input-${item.item_id}`}
                        />
                      </div>
                      <Button
                        onClick={() => addToBasket(item)}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all h-9 px-4 flex-1"
                        data-testid={`add-to-basket-${item.item_id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('common.add')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-testid="user-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Desktop Layout */}
          <div className="hidden md:flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Physidia - Nordic Medcom</h1>
                <p className="text-sm text-gray-600">{user.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <ReminderBell 
                enabled={user.reminder_enabled || false} 
                onReminderChange={(val) => onUserUpdate && onUserUpdate({ ...user, reminder_enabled: val })}
              />
              <LanguageSelector onLanguageChange={handleLanguageChange} />
              <Button
                onClick={() => setShowBasket(!showBasket)}
                variant="outline"
                className="relative hover:bg-blue-50 hover:border-blue-300 transition-colors"
                data-testid="basket-button"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {t('order.basket')}
                {basket.length > 0 && (
                  <Badge className="ml-2 bg-red-500 text-white" data-testid="basket-count">{basket.length}</Badge>
                )}
              </Button>
              <Button
                onClick={() => setShowLogoutConfirm(true)}
                variant="outline"
                className="hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
                data-testid="logout-button"
              >
                {t('auth.logout')}
              </Button>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-sm font-bold text-gray-900 leading-tight">Physidia -<br/>Nordic Medcom</h1>
                  <p className="text-xs text-gray-600">{user.name}</p>
                </div>
              </div>
              <div className="flex flex-col space-y-2">
                <div className="flex space-x-1 items-center">
                  <ReminderBell 
                    enabled={user.reminder_enabled || false} 
                    onReminderChange={(val) => onUserUpdate && onUserUpdate({ ...user, reminder_enabled: val })}
                  />
                  <LanguageSelector onLanguageChange={handleLanguageChange} />
                  <Button
                    onClick={() => setShowLogoutConfirm(true)}
                    variant="outline"
                    size="sm"
                    className="hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors h-8 px-3"
                    data-testid="logout-button"
                  >
                    {t('auth.logout')}
                  </Button>
                </div>
                <Button
                  onClick={() => setShowBasket(!showBasket)}
                  variant="outline" 
                  size="sm"
                  className="relative hover:bg-blue-50 hover:border-blue-300 transition-colors h-8 px-3"
                  data-testid="basket-button"
                >
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  {t('order.basket')}
                  {basket.length > 0 && (
                    <Badge className="ml-1 bg-red-500 text-white text-xs" data-testid="basket-count">{basket.length}</Badge>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showBasket ? (
          <Card className="shadow-lg border-0" data-testid="basket-view">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-2xl md:text-2xl text-sm">{t('order.basket')}</CardTitle>
                  <CardDescription>{basket.length} {t('nav.items').toLowerCase()}</CardDescription>
                </div>
                <Button
                  onClick={() => setShowBasket(false)}
                  variant="outline"
                  data-testid="back-to-items-button"
                >
                  {t('common.back')} {t('nav.items')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {basket.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg">{t('order.emptyBasket')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {basket.map((item) => (
                    <div
                      key={item.item_id}
                      className="p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow"
                      data-testid={`basket-item-${item.item_id}`}
                    >
                      {/* Desktop Layout */}
                      <div className="hidden md:flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{item.item_name}</h3>
                          <p className="text-sm text-gray-600">{item.vendor_name}</p>
                          <p className="text-sm text-blue-600 font-medium">
                            {t('item.ordered')}: {item.quantity} | {t('item.total')}: {calculateTotalUnits(item.units, item.quantity)} {item.units ? `(${item.units})` : ''}
                          </p>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateBasketQuantity(item.item_id, -1)}
                            data-testid={`decrease-quantity-${item.item_id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="font-semibold text-lg w-8 text-center" data-testid={`quantity-${item.item_id}`}>{item.quantity}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateBasketQuantity(item.item_id, 1)}
                            data-testid={`increase-quantity-${item.item_id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => removeFromBasket(item.item_id)}
                            data-testid={`remove-item-${item.item_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Mobile Layout */}
                      <div className="md:hidden space-y-3">
                        {/* Line 1: Item name and vendor */}
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">{item.item_name}</h3>
                          <p className="text-xs text-gray-600">{item.vendor_name}</p>
                          <p className="text-xs text-blue-600 font-medium">
                            {t('item.ordered')}: {item.quantity} | {t('item.total')}: {calculateTotalUnits(item.units, item.quantity)} {item.units ? `(${item.units})` : ''}
                          </p>
                        </div>
                        
                        {/* Line 2: Quantity controls and delete button */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateBasketQuantity(item.item_id, -1)}
                              data-testid={`decrease-quantity-${item.item_id}`}
                              className="h-8 w-8 p-0"
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="font-semibold text-base w-8 text-center" data-testid={`quantity-${item.item_id}`}>{item.quantity}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateBasketQuantity(item.item_id, 1)}
                              data-testid={`increase-quantity-${item.item_id}`}
                              className="h-8 w-8 p-0"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => removeFromBasket(item.item_id)}
                            data-testid={`remove-item-${item.item_id}`}
                            className="h-8 px-3"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    onClick={handleCheckout}
                    disabled={ordering}
                    className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                    data-testid="checkout-button"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {ordering ? t('common.loading') : t('order.placeOrder')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Search and Show Basket */}
            <Card className="shadow-lg border-0">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      placeholder={t('common.search') + '...'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-12 text-base pl-10 pr-10"
                      data-testid="search-input"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                        data-testid="clear-search-button"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  {/* Desktop only Show Basket button */}
                  <Button
                    onClick={() => setShowBasket(true)}
                    variant="outline"
                    className="hidden md:flex h-12 px-6 relative hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    data-testid="show-basket-button"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    {t('order.viewBasket')}
                    {basket.length > 0 && (
                      <Badge className="ml-2 bg-red-500 text-white" data-testid="basket-count-search">{basket.length}</Badge>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Top 10 Most Ordered Items */}
            {renderItemsGroup(groupedTop10, t('item.topOrdered'), true)}

            {/* Regular Items */}
            {renderItemsGroup(groupedRegular, Object.keys(groupedTop10).length > 0 ? t('common.all') + ' ' + t('nav.items') : null, false)}

            {/* No items message */}
            {Object.keys(groupedTop10).length === 0 && Object.keys(groupedRegular).length === 0 && (
              <Card className="shadow-lg border-0">
                <CardContent className="py-12 text-center text-gray-500">
                  <p className="text-lg">{t('item.noItemsYet')}</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('messages.confirmLogout')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('order.emptyBasket')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onLogout} className="bg-red-600 hover:bg-red-700">
              {t('auth.logout')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Order Confirmation Dialog */}
      <AlertDialog open={showOrderConfirm} onOpenChange={setShowOrderConfirm}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('order.confirmOrder')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('order.confirmOrderMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-60 overflow-y-auto my-4">
            <div className="space-y-3">
              {basket.map((item) => (
                <div key={item.item_id} className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-sm">{item.item_name}</p>
                  <p className="text-xs text-gray-600">{item.vendor_name}</p>
                  <p className="text-xs text-blue-600 font-medium">
                    {t('item.ordered')}: {item.quantity} | {t('item.total')}: {calculateTotalUnits(item.units, item.quantity)} {item.units ? `(${item.units})` : ''}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={ordering}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmCheckout} 
              disabled={ordering}
              className="bg-green-600 hover:bg-green-700"
            >
              {ordering ? t('common.loading') : t('order.confirmOrder')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}